"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const RULES = [
  {
    topic: "Save posts immediately",
    rule: "There is no Draft, Posted, Mark As, or Save & Post. Save writes the journal and posts ledger lines. Edit re-syncs that journal. Delete reverses balances and removes the ledger journal",
  },
  {
    topic: "Subscription gate",
    rule: "Creating is limited by the Manual Journal plan feature. Editing and deleting are not separately gated that way",
  },
  {
    topic: "Journal number",
    rule: "Auto-generated unless you turn that off under Configure Manual Journal Number Preferences. Default prefix MJ. Locked after save when auto-generate is on",
  },
  {
    topic: "Debits must equal credits",
    rule: "The form shows Difference in red when they do not match. Save is not disabled; the API rejects: Total debits and total credits must be equal. Each line is debit or credit, not both",
  },
  {
    topic: "Lines",
    rule: "Starts with two rows. Add Row available. You cannot delete below two rows in the UI. Account is a Chart of Accounts picker. Description optional",
  },
  {
    topic: "AR / AP contact",
    rule: "Column Contact (INR) is enabled after Account. Accounts Receivable → Select Customer (required). Accounts Payable → Select Vendor (required). Other accounts → Select Contact, optional. Options are customers and vendors of the branch — not Account Ledger masters. API: Vendor must be chosen for an Accounts Payable account / Customer must be chosen for an Accounts Receivable account",
  },
  {
    topic: "Division, Branch, Notes, Date, Currency",
    rule: "Division, Branch, Notes, Date, Currency, and Journal# are required. Warehouse and Reference# are optional. No exchange-rate field. Reporting Method is sent as accrual-and-cash and shown on detail, not on Add",
  },
  {
    topic: "No primary bank",
    rule: "Unlike Quote and Invoice, you can save a journal without a primary bank",
  },
  {
    topic: "Edit any saved journal",
    rule: "There is no draft-only gate and no period-close block on this module. Edit from the list. Detail has Download and Print, not Edit",
  },
  {
    topic: "Not invoice journals",
    rule: "Automatic posts from Invoice, Bill, Payment, Credit Note, Opening Balance live in the ledger service. They are not Manual Journal documents and cannot be edited here",
  },
  {
    topic: "No Duplicate, no list Export, no Reverse, no Recurring, no GST",
    rule: "Attachments: Attach Files(s) to Manual Journal — up to 3 files, 5 MB each",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. Creating a manual journal" },
      { heading: "2. Lines — accounts, debit, credit, contact" },
      { heading: "3. Viewing, searching, and the list" },
      { heading: "4. Editing a journal" },
      { heading: "5. Printing and downloading" },
      { heading: "6. Deleting a journal" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function ManualJournalsPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Manual Journals
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone posting an entry that no invoice, bill, or payment
            created — depreciation, reclass, a correction
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            A <B>Manual Journal</B> is a user voucher. There is only{" "}
            <B>Save</B> — that posts the ledger immediately. Debits must
            equal credits. Prefix <B>MJ</B>. Automatic journals from sales
            and purchase documents are not this module and cannot be edited
            here.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Creating a manual journal
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Add → Add Manual Journal. Pick <B>Division</B> then{" "}
            <B>Branch</B> (contacts load from branch).
          </p>
          <Definitions
            items={[
              {
                term: "Manual Journal#",
                desc: (
                  <>
                    Required. Gear:{" "}
                    <B>Configure Manual Journal Number Preferences</B>.
                    Default prefix <B>MJ</B>.
                  </>
                ),
              },
              {
                term: "Date",
                desc: <>Required. Posting date. Defaults to today.</>,
              },
              {
                term: "Notes",
                desc: <>Required. Narration of why this entry exists.</>,
              },
              {
                term: "Currency",
                desc: (
                  <>
                    Required. Defaults to INR. No exchange-rate field on
                    Add.
                  </>
                ),
              },
              {
                term: "Reference#, Warehouse",
                desc: (
                  <>
                    Optional. Reference# is on the form, not on the detail
                    header. Warehouse filtered by branch.
                  </>
                ),
              },
            ]}
          />
          <Callout type="note">
            Reporting Method is not on Add. Detail shows it as
            accrual-and-cash.
          </Callout>
          <Screenshot caption="Screenshot: Add Manual Journal with Division, Branch, Journal#, Date, Notes, Currency, and Save." />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Lines — accounts, debit, credit, contact
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Two empty rows to start. <B>Add Row</B>. Totals show Total and{" "}
            <B>Difference</B> (red if not zero).
          </p>
          <Definitions
            items={[
              {
                term: "Account",
                desc: (
                  <>
                    Chart of Accounts picker. Bank and locked system
                    accounts are not excluded in this picker.
                  </>
                ),
              },
              {
                term: "Description",
                desc: <>Optional per line.</>,
              },
              {
                term: "Contact (INR)",
                desc: (
                  <>
                    Enabled after Account. AR: <B>Select Customer</B>{" "}
                    (required). AP: <B>Select Vendor</B> (required).
                    Otherwise <B>Select Contact</B> (optional). Loaded from
                    the branch — customers and vendors, not Account Ledger
                    masters.
                  </>
                ),
              },
              {
                term: "Debits / Credits",
                desc: (
                  <>
                    One side per line. Save is rejected until both sides of
                    the voucher match.
                  </>
                ),
              },
              {
                term: "Attachments",
                desc: (
                  <>
                    <B>Attach Files(s) to Manual Journal</B> — 3 files, 5
                    MB each.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: journal lines with Account, Debits, Credits, Difference, and Add Row." />

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Viewing, searching, and the list
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Title Manual Journals with a count. Add. Search, column picker,
            View Filters (date presets/range, Division, Branch), filter
            lock, Apply Filters. Columns: Journal#, Date, Branch, VCH NO,
            Notes, Total Amount, Created By, Updated By. Action: View
            Journal, Edit Journal, Delete Journal. Row click → detail. No
            TOOLS export, no Duplicate, no status tabs.
          </p>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Detail title Manual Journal Details. Header: Journal#, Voucher
            No. Journal Details: Date, Reporting Method, Currency, Division,
            Branch, Warehouse, Notes, Created By. Journal Entries table plus
            attachments and History when present.
          </p>
          <Screenshot caption="Screenshot: Manual Journals list with filters and View / Edit / Delete." />

          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Editing a journal
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            From the list, not the detail header. Any saved journal can be
            edited. Journal# does not change. Save re-posts the ledger.
            There is no closed-period block on this screen.
          </p>
          <Screenshot caption="Screenshot: Edit Manual Journal from the list action." />

          <h3
            id="section-0-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. Printing and downloading
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Detail: <B>Download</B> (PDF) and <B>Print</B>.
          </p>

          <h3
            id="section-0-sub-5"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            6. Deleting a journal
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            List <B>Delete Journal</B>. Reverses posted amounts and removes
            the ledger journal. There is no Cancel or Reverse voucher that
            keeps a cancelled copy.
          </p>
          <Callout type="warning">
            To see the effect on Sales or Rent, open Chart of Accounts and
            click that account. Sidebar Account Ledger is a different
            master.
          </Callout>
          <Screenshot caption="Screenshot: Delete Journal on the list." />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/accountant/chart-of-accounts"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Chart of Accounts <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Accounts on each line, and the statement after you click a row
            </p>
          </Link>
          <Link
            href="/documentation/books/accountant/account-ledger"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Account Ledger <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Named masters — not where this journal&apos;s lines appear
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
